/**
 * 管理パネル（ドック）のタブの中身と、全国のポートフォリオ。
 * サンプル WebUI-Sample/3D-Portal-Google3D.html の renderDock（S777-788）・renderEarthTab（S820-859）・
 * renderPlanTab（S860-901）・renderConstTab（S903-929）・renderSchedTab（S930-983）・
 * renderWMTab（S984-1029）・portfolioHTML（S1030-1050）と同じ HTML（クラス名・見出し・行・文言・書式・並び）を返す。
 *
 * サンプルとの違い:
 * - 入力欄の目印は portal の処理に合わせる（施工計画は data-plan、計画地盤高・根切りは data-grade、土質は data-soil）。
 *   範囲は src/app/planInput.ts の PLAN_FIELDS・GRADE_FIELDS。読み上げのため aria-label も持つ。
 * - ボタンは onclick ではなく data-act で動作を表す（portal が処理する）。値は DOCK_ACTS を参照。
 * - 表示期間を越える予測日には「（表示期間外）」を添える（計算値は変えない）。
 * - 切盛分布図は canvas ではなく、マスを 1 画素にした PNG の img（サンプルの canvas と同じ拡大のしかた）に
 *   枠・ゲート・N↑ の SVG を重ねる。詳細は src/ui/siteCharts.ts の cutFillMap を参照。
 * 文字はすべて escapeHtml を通し、色は safeColor を通す。innerHTML に入れる前提。
 */
import type { SiteView, Store } from '../app/store';
import { GRADE_FIELDS, PLAN_FIELDS } from '../app/planInput';
import type { FieldRange, GradeKey, PlanNumberKey } from '../app/planInput';
import {
  escapeHtml as esc,
  formatFixed1 as fmt1,
  formatInt as fmtN,
  safeColor,
} from '../core/text';
import { formatDay as ds } from '../core/time';
import type { DayNumber } from '../core/time';
import type { SoilKey } from '../data/types';
import { B, REGIONS, SOILS, T_MAX, stateAt, taskProgress, trafficGuards } from '../model';
import type { Region } from '../model';
import {
  constructionSteps,
  cutFillMap,
  delayChain,
  massCurve,
  massNote,
  sCurve,
  siteGantt,
} from './siteCharts';
import { portfolioGantt } from './charts';

export type DockTab = 'earth' | 'plan' | 'const' | 'sched' | 'wm';

/** タブの並びと名前（サンプルの TABS）。 */
export const DOCK_TABS: readonly (readonly [DockTab, string])[] = [
  ['earth', '土量算出'],
  ['plan', '土工計画'],
  ['const', '施工計画'],
  ['sched', '工程管理'],
  ['wm', 'ワールドモデル'],
];

/**
 * ボタンの data-act の値。portal が処理する。
 * - `tab:<タブ>` … タブを切りかえる（タブの見出し、ワールドモデルの「編成を変更」「計画地盤高を変更」）
 * - `dock` … ドックを閉じる／開く
 * - `reset` … 初期条件／初期計画に戻す
 * - `play` … 基準日から予測を再生する
 * - `layer:heat`・`layer:crane`・`layer:route`・`layer:design` … 3D のレイヤーを入れる
 */
export const DOCK_ACTS = [
  ...DOCK_TABS.map(([k]) => `tab:${k}` as const),
  'dock',
  'reset',
  'play',
  'layer:heat',
  'layer:crane',
  'layer:route',
  'layer:design',
] as const;
export type DockAct = (typeof DOCK_ACTS)[number];

const winterTxt = (r: Region): string =>
  r.ws ? `${r.ws}月〜${r.we}月（土工稼働 ${Math.round(r.wf * 100)}%）` : 'なし';
/** 予測の日付。表示期間を越える日は言葉を添える（サンプルにはない）。 */
const fds = (d: DayNumber): string => (d > T_MAX ? `${ds(d)}（表示期間外）` : ds(d));
const plus = (d: number): string => (d > 0 ? '+' : '');

// ---- 入力欄（サンプルの FIELDS・fieldHTML） ----

/** サンプルの欄の名前（字下げの全角空白も含めてサンプルどおり）。範囲は planInput から取る。 */
const PLAN_LABELS: Readonly<Record<PlanNumberKey, string>> = {
  usable: '流用可能率',
  cap: 'ダンプ積載（ほぐし）',
  ex: 'バックホウ（0.7m³級）',
  qEx: '　日当り掘削積込',
  trucks: 'ダンプトラック（10t）',
  dExt: '場外運搬距離',
  rollers: 'ブルドーザ＋ローラ',
  qR: '　日当り締固め',
  hours: '稼働時間',
  days: '稼働日',
  rain: '雨天・休止率',
  winterF: '冬期の土工稼働率',
};

function numberField(
  attr: 'plan' | 'grade',
  k: string,
  r: FieldRange,
  value: number,
  label: string,
): string {
  return `<label for="f_${esc(k)}">${esc(label)}</label><span class="in"><input id="f_${esc(k)}" type="number" data-${attr}="${esc(k)}" min="${r.min}" max="${r.max}" step="${r.step}" value="${Math.round(value * 10) / 10}" aria-label="${esc(label.trim())}">${esc(r.unit)}</span>`;
}

/** 施工計画の数値の欄。 */
export function planField(v: SiteView, k: PlanNumberKey): string {
  return numberField('plan', k, PLAN_FIELDS[k], v.plan[k], PLAN_LABELS[k]);
}

/** 計画地盤高・根切り深さの欄。label を省くと「計画地盤高 FH」「根切り深さ」。 */
export function gradeField(v: SiteView, k: GradeKey, label = GRADE_FIELDS[k].label): string {
  return numberField('grade', k, GRADE_FIELDS[k], v.site[k], label);
}

/** 土質の欄。 */
export function soilField(v: SiteView): string {
  const opts = (Object.keys(SOILS) as SoilKey[])
    .map((key) => {
      const so = SOILS[key];
      return `<option value="${key}"${v.plan.soil === key ? ' selected' : ''}>${esc(so.name)}（L=${so.L.toFixed(2)}／C=${so.C.toFixed(2)}）</option>`;
    })
    .join('');
  return `<label for="f_soil">土質</label><span class="in"><select id="f_soil" data-soil="soil" aria-label="土質">${opts}</select></span>`;
}

/** 初期計画から変えているかの札（サンプルの whatIfNote）。 */
export function whatIfNote(changed: boolean): string {
  return changed
    ? '<span class="badge warn" style="margin-left:0">What-if 適用中</span>'
    : '<span class="badge" style="margin-left:0">初期計画（較正済）</span>';
}

// ---- ドックの見出し（サンプルの renderDock） ----

/** ドックの見出し（現場 ID かポートフォリオの名前、タブ、閉じるボタン）。 */
export function dockHeaderHtml(
  v: SiteView | null | undefined,
  tab: DockTab,
  store: Store,
  open = true,
): string {
  const pref = store.state.pref;
  const title = v
    ? `<span class="dtitle"><span class="dot" style="background:${safeColor(v.site.color)}"></span> ${esc(v.site.id)}</span>`
    : `<span class="dtitle">${pref ? esc(pref) : '全国'}のポートフォリオ</span>`;
  const tabs = DOCK_TABS.map(
    ([k, n]) =>
      `<button class="dtab${v && tab === k ? ' on' : ''}" data-tab="${k}" data-act="tab:${k}" ${v ? '' : 'disabled'}>${n}</button>`,
  ).join('');
  return `${title}${tabs}<button class="dclose" id="dclose" data-act="dock">${open ? '▼ 閉じる' : '▲ 開く'}</button>`;
}

/** ドックの本体。現場を選んでいなければポートフォリオ、選んでいればタブの中身。 */
export function dockTabsHtml(v: SiteView | null | undefined, tab: DockTab, store: Store): string {
  if (!v) return portfolioHtml(store);
  const T = store.state.T;
  const changed = store.isWhatIf(v);
  switch (tab) {
    case 'earth':
      return earthTabHtml(v, changed);
    case 'plan':
      return planTabHtml(v, changed);
    case 'const':
      return constTabHtml(v, T, changed);
    case 'sched':
      return schedTabHtml(v, T, changed);
    case 'wm':
      return wmTabHtml(v, T);
  }
}

// ---- 土量算出（サンプルの renderEarthTab） ----

export function earthTabHtml(v: SiteView, changed: boolean): string {
  const s = v.site;
  const g = s.g;
  return `<div class="g3">
    <div class="card"><h3>切盛土量 <small>メッシュ法（四隅点高）・実地形</small></h3><div id="earthKpi">${earthKpiHtml(v)}</div></div>
    <div class="card"><h3>切盛分布図 <small>計画面 − 現況</small></h3><div id="heat">${cutFillMap(v)}</div>
      <div class="hleg"><span>盛土</span><span class="grad"></span><span>切土</span><span>±4m</span></div>
      <div class="note">現況：国土地理院 ${esc(s.terrainSrc)}（${fmt1(g.dx)}m格子に補間、${g.nx}×${g.nz}セル）。計画面：計画地盤高＋${esc(s.kind.pitLabel)}、外周 ${s.kind.mD}m 幅で現況へすり付け（法面）。白枠は構造物、▲はゲート。</div></div>
    <div class="card"><h3>算出条件 ${whatIfNote(changed)}</h3>
      <div class="form">${gradeField(v, 'FH')}${s.kind.pit ? gradeField(v, 'pit', s.kind.pitLabel) : ''}<div class="sep"></div>${soilField(v)}${planField(v, 'usable')}${planField(v, 'cap')}</div>
      <div id="earthBal" style="margin-top:10px">${earthBalHtml(v)}</div>
      <div class="btns" style="margin-top:8px"><button class="btn ghost" id="resetFleet" data-act="reset">初期条件に戻す</button><button class="btn ghost" id="heatOn" data-act="layer:heat">3Dでヒートマップ</button></div></div></div>`;
}

export function earthKpiHtml(v: SiteView): string {
  const eb = v.cur.eb;
  return `<div class="tiles">
    <div class="tile cut"><div class="v">${fmtN(eb.Vc)}<small>m³</small></div><div class="l">切土量（地山）</div></div>
    <div class="tile fill"><div class="v">${fmtN(eb.Vf)}<small>m³</small></div><div class="l">盛土量（締固め後）</div></div>
    <div class="tile"><div class="v">${fmtN(eb.needNat)}<small>m³</small></div><div class="l">盛土に要する地山量 Vf/C</div></div>
    <div class="tile"><div class="v ${eb.sur >= 0 ? '' : 'warnc'}">${eb.sur >= 0 ? '+' : ''}${fmtN(eb.sur)}<small>m³</small></div><div class="l">${eb.sur >= 0 ? '流用後の余剰（地山）' : '不足＝購入土（地山）'}</div></div>
    <div class="tile"><div class="v">${fmtN(eb.outLoose)}<small>m³</small></div><div class="l">残土搬出量（ほぐし）</div></div>
    <div class="tile"><div class="v">${fmtN(eb.tripsOut + eb.tripsIn)}<small>台</small></div><div class="l">場外ダンプ 延べ台数</div></div></div>`;
}

export function earthBalHtml(v: SiteView): string {
  const eb = v.cur.eb;
  return `<table class="t"><tr><th>項目</th><th class="n">数量</th><th>式</th></tr>
    <tr><td>流用土（地山）</td><td class="n">${fmtN(eb.usableNat)}</td><td>Vc×${(eb.u * 100).toFixed(0)}%</td></tr>
    <tr><td>場外搬出（ほぐし）</td><td class="n">${fmtN(eb.outLoose)}</td><td>（余剰＋不適）×L ${eb.so.L.toFixed(2)}</td></tr>
    <tr><td>購入土（ほぐし）</td><td class="n">${fmtN(eb.borrowLoose)}</td><td>不足×L</td></tr>
    <tr><td>取扱土量 Q（地山）</td><td class="n">${fmtN(eb.Q)}</td><td>Vc＋購入土</td></tr></table>`;
}

// ---- 土工計画（サンプルの renderPlanTab） ----

const WORK_KEYS: readonly PlanNumberKey[] = [
  'ex',
  'qEx',
  'trucks',
  'dExt',
  'rollers',
  'qR',
  'hours',
  'days',
];

export function planTabHtml(v: SiteView, changed: boolean): string {
  const s = v.site;
  return `<div class="g3">
    <div class="card"><h3>機械編成・稼働条件 ${whatIfNote(changed)}</h3>
      <div class="form">${WORK_KEYS.map((k) => planField(v, k)).join('')}<div class="sep"></div>${planField(v, 'rain')}${s.region.ws ? planField(v, 'winterF') : ''}</div>
      <div class="note">雨天・休止率・冬期稼働率・運搬距離の初期値は ${esc(s.pref)} の地域係数（サンプル値）。冬期：${esc(winterTxt(s.region))}</div>
      <div class="btns" style="margin-top:8px"><button class="btn ghost" id="resetFleet" data-act="reset">初期計画に戻す</button></div></div>
    <div class="card"><h3>施工能力とボトルネック <small>地山 m³/日</small></h3><div id="capBox">${capHtml(v)}</div></div>
    <div class="card"><h3>土積曲線（マスカーブ） <small>締固め換算 m³</small></h3><div id="massBox">${massCurve(v)}<div class="note">${massNote(v)}</div></div></div></div>`;
}

export function capHtml(v: SiteView): string {
  const s = v.site;
  const c = v.cur.cap;
  const r = v.cur.res;
  const eb = v.cur.eb;
  const max = Math.max(...c.list.map(([, x]) => (Number.isFinite(x) ? x : 0)), 1);
  const t = s.tasks[s.earthIdx];
  const ef = v.cur.fc[s.earthIdx];
  const bars = c.list
    .map(
      ([n, x]) =>
        `<div class="cbar${x === c.min ? ' bn' : ''}"><span>${esc(n)}</span><div class="track"><i style="width:${Number.isFinite(x) ? (x / max) * 100 : 100}%"></i></div><span class="n">${Number.isFinite(x) ? fmtN(x) : '—'}</span></div>`,
    )
    .join('');
  const etaTxt =
    s.etaSrc === 'obs'
      ? `実績から推定（基準日まで ${fmtN(s.achieved ?? Number.NaN)} m³/稼働日）`
      : s.etaSrc === 'done'
        ? '土工完了済（参考値）'
        : '観測なし：既定値';
  const status =
    !t || !ef
      ? '—'
      : t.a >= 1
        ? '<b class="ahead">土工完了（実績）</b>'
        : `完了予測 <b>${fds(ef.F)}</b>（計画 ${ds(t.te)}、<span class="${ef.F > t.te ? 'late' : 'ahead'}">${plus(ef.F - t.te)}${ef.F - t.te}日</span>）`;
  return `${bars}<table class="t" style="margin-top:8px">
    <tr><td>ボトルネック</td><td><b class="warnc">${esc(c.bn)}</b></td></tr>
    <tr><td>実効率 η</td><td>${s.eta.toFixed(2)}（${etaTxt}）</td></tr>
    <tr><td>実効施工量</td><td>${fmtN(r.rate)} m³/稼働日</td></tr>
    <tr><td>土工所要</td><td>全体 ${fmtN(r.workdays)} 稼働日／残 ${fmtN(r.remWork)} 稼働日</td></tr>
    <tr><td>土工</td><td>${status}</td></tr>
    <tr><td>運搬サイクル</td><td>場内 ${fmt1(c.cycInt)} 分（平均 ${fmtN(c.dInt)} m）／場外 ${fmt1(c.cycExt)} 分</td></tr>
    <tr><td>場外ダンプ</td><td>${fmt1(r.dailyExt)} 台/日（ゲート通過 往復 ${fmtN(r.dailyExt * 2)} 台/日）</td></tr></table>
    <div class="note">取扱土量 Q=${fmtN(eb.Q)} m³。稼働日は地域係数（雨天・冬期）で日々変化し、冬期は土工の進みが遅くなります。</div>`;
}

// ---- 施工計画（サンプルの renderConstTab） ----

export function constTabHtml(v: SiteView, T: DayNumber, changed: boolean): string {
  const s = v.site;
  const p = v.plan;
  const r = v.cur.res;
  const eb = v.cur.eb;
  const cap = v.cur.cap;
  const rg = s.region;
  const per = 2 * (s.area.w + s.area.d);
  const ef = s.tasks[s.earthIdx];
  const act = ef !== undefined && ef.a < 1;
  const winterNow = stateAt(s, T, v.cur).winter && rg.ws;
  const crane = s.crane
    ? `<tr><td>タワークレーン</td><td>作業半径 ${s.crane.jib} m／高さ ${Math.round(s.crane.h)} m</td></tr><tr><td>立入禁止範囲</td><td>半径 ${s.crane.jib + 5} m（3D「揚重範囲」）</td></tr>`
    : '<tr><td>揚重</td><td>移動式クレーン（必要時に配置計画）</td></tr>';
  return `<div class="g3">
    <div class="card"><h3>施工手順 <small>${ds(T)} 時点・予測工程</small></h3>${constructionSteps(v, T)}</div>
    <div class="card"><h3>地域条件・仮設・安全</h3><table class="t">
      <tr><td>所在地</td><td>${esc(s.pref)}${esc(s.city)}</td></tr>
      <tr><td>雨天・休止率</td><td>${p.rain}%（地域係数 ${rg.rain}%）</td></tr>
      <tr><td>冬期</td><td>${rg.ws ? `${rg.ws}月〜${rg.we}月・土工稼働 ${p.winterF}%` : '積雪・凍結による休止なし'}${winterNow ? ' <b class="warnc">（表示時点は冬期）</b>' : ''}</td></tr>
      <tr><td>残土運搬距離</td><td>${p.dExt} km（地域標準 ${rg.dExt} km）</td></tr>
      <tr><td>仮囲い延長</td><td>約 ${fmtN(per)} m</td></tr>${crane}
      <tr><td>工事車両</td><td>${act ? `場外ダンプ ${fmt1(r.dailyExt)} 台/日・往復 ${fmtN(r.dailyExt * 2)} 台/日` : '土工完了・資材搬入のみ'}</td></tr>
      <tr><td>交通誘導員</td><td>${act ? trafficGuards(r) : 1} 名（ゲート配置の目安）</td></tr></table>
      <div class="btns" style="margin-top:8px"><button class="btn ghost" data-ly="crane" data-act="layer:crane">3Dで揚重範囲</button><button class="btn ghost" data-ly="route" data-act="layer:route">3Dで搬出ルート</button><button class="btn ghost" data-ly="design" data-act="layer:design">3Dで計画面</button></div></div>
    <div class="card"><h3>主要機械編成（土工） ${whatIfNote(changed)}</h3><table class="t">
      <tr><th>機械</th><th class="n">台数</th><th class="n">能力</th></tr>
      <tr><td>バックホウ 0.7m³級</td><td class="n">${p.ex}</td><td class="n">${fmtN(cap.ex)} m³/日</td></tr>
      <tr><td>ダンプトラック 10t</td><td class="n">${p.trucks}</td><td class="n">${fmtN(cap.truck)} m³/日</td></tr>
      <tr><td>ブルドーザ＋ローラ</td><td class="n">${p.rollers}</td><td class="n">${Number.isFinite(cap.roller) ? `${fmtN(cap.roller)} m³/日` : '—'}</td></tr></table>
      <table class="t" style="margin-top:8px"><tr><td>土質</td><td>${esc(eb.so.name)}（L ${eb.so.L.toFixed(2)}／C ${eb.so.C.toFixed(2)}）</td></tr>
      <tr><td>燃料（土工全体）</td><td>約 ${fmtN(r.fuel / 1000)} kL</td></tr><tr><td>CO₂（概算）</td><td>約 ${fmt1(r.co2)} t-CO₂</td></tr><tr><td>運搬走行距離</td><td>約 ${fmtN(r.truckKm)} km</td></tr></table>
      <div class="note">燃料原単位はサンプル値（バックホウ15 L/h、ローラ8 L/h、ダンプ3.5 km/L、軽油2.58 kg-CO₂/L）。</div></div></div>`;
}

// ---- 工程管理（サンプルの renderSchedTab） ----

export function schedTabHtml(v: SiteView, T: DayNumber, changed: boolean): string {
  return `<div class="g2"><div class="card"><h3>ガントチャート <small>灰＝計画／色＝実績・予測（濃色は基準日実績）。クリックでワールド時刻を移動</small></h3><div id="ganttBox">${siteGantt(v, T)}</div></div>
    <div class="card"><h3>出来高曲線（Sカーブ）</h3><div id="scurveBox">${sCurve(v, T, changed)}</div><div id="schedNote">${schedNoteHtml(v)}</div></div></div>`;
}

export function schedNoteHtml(v: SiteView): string {
  const s = v.site;
  const d = v.cur.delay;
  return `<table class="t" style="margin-top:6px"><tr><td>計画竣工</td><td>${ds(s.planEnd)}</td></tr>
    <tr><td>予測竣工</td><td><b class="${d > 14 ? 'late' : d > 0 ? 'warnc' : 'ahead'}">${fds(v.cur.end)}（${plus(d)}${d}日）</b></td></tr>
    <tr><td>遅延の連鎖</td><td>${esc(delayChain(v))}</td></tr>${s.region.ws ? `<tr><td>冬期</td><td>${esc(winterTxt(s.region))}</td></tr>` : ''}</table>`;
}

// ---- ワールドモデル（サンプルの renderWMTab） ----

export function wmTabHtml(v: SiteView, T: DayNumber): string {
  return `<div class="g3">
    <div class="card"><h3>ワールドモデルの構成 <small>観測→状態推定→遷移→予測→描画</small></h3>${wmDiagram()}
      <div class="note">状態 S(t)＝実地形（ABR座標×標高タイル）・出来形・重機配置。基準日までは実績で較正（η・SPI）。以降は施工計画（行動 a）と地域係数（雨天・冬期）を含む遷移関数で日単位に予測します。</div></div>
    <div class="card"><h3>状態ベクトル S(t) <small>タイムラインに連動</small></h3><div id="wmState">${wmStateHtml(v, T)}</div></div>
    <div class="card"><h3>What-if（行動 a の比較）</h3><div id="wmWhatIf">${wmWhatIfHtml(v)}</div>
      <div class="btns" style="margin-top:8px"><button class="btn" id="goPlan" data-act="tab:plan">編成を変更</button><button class="btn ghost" id="goEarth" data-act="tab:earth">計画地盤高を変更</button><button class="btn ghost" id="resetFleet" data-act="reset">初期計画に戻す</button><button class="btn ghost" id="playFwd" data-act="play">予測を再生</button></div></div></div>`;
}

/** ワールドモデルの構成図（サンプルの wmDiagram。固定の図）。 */
export function wmDiagram(): string {
  const box = (x: number, y: number, w: number, a: string, b: string, c: string) =>
    `<rect x="${x}" y="${y}" width="${w}" height="38" rx="8" fill="#1b2945" stroke="${c}"/><text x="${x + w / 2}" y="${y + 16}" font-size="11" fill="#fff" text-anchor="middle" font-weight="600">${esc(a)}</text><text x="${x + w / 2}" y="${y + 30}" font-size="9.5" fill="#93a1bd" text-anchor="middle">${esc(b)}</text>`;
  const ar = (x1: number, y1: number, x2: number, y2: number) =>
    `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#64748b" stroke-width="1.5" marker-end="url(#ah)"/>`;
  return `<svg class="chart" viewBox="0 0 360 210" role="img" aria-label="ワールドモデル構成図"><defs><marker id="ah" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 0L10 5L0 10z" fill="#64748b"/></marker></defs>
    ${box(8, 10, 104, '観測 O(t)', '実績・ABR・標高', '#22c55e')}${box(128, 10, 104, '状態推定', 'η・SPI の較正', '#22c55e')}${box(248, 10, 104, '状態 S(t)', '実地形・出来形・重機', '#60a5fa')}
    ${box(248, 86, 104, '遷移 f(S,a,r)', '能力×η×地域係数', '#f59e0b')}${box(128, 86, 104, '予測 S(t+Δ)', '日単位ロールアウト', '#f59e0b')}${box(8, 86, 104, '行動 a', '編成・計画地盤高', '#e879f9')}
    ${box(68, 162, 224, '描画・意思決定', '全国／3D／工程／土量／What-if', '#94a3b8')}
    ${ar(112, 29, 128, 29)}${ar(232, 29, 248, 29)}${ar(300, 48, 300, 86)}${ar(248, 105, 232, 105)}${ar(112, 105, 248, 112)}${ar(180, 124, 180, 162)}</svg>`;
}

/** 状態ベクトル S(t)（サンプルの wmStateHTML）。 */
export function wmStateHtml(v: SiteView, T: DayNumber): string {
  const s = v.site;
  const p = v.plan;
  const st = stateAt(s, T, v.cur);
  const eb = v.cur.eb;
  const e = st.e;
  const winter = st.winter && s.region.ws;
  const rows: [string, string][] = [];
  rows.push(['時刻 t', `${ds(T)}（${T > B ? '予測' : '実績'}）${winter ? '・冬期' : ''}`]);
  rows.push([
    '全体進捗',
    `${(st.overall * 100).toFixed(1)}%（計画 ${(st.plan * 100).toFixed(1)}%）`,
  ]);
  rows.push(['土工進捗 e', `${(e * 100).toFixed(1)}%`]);
  rows.push(['切土 累計', `${fmtN(eb.Vc * e)} / ${fmtN(eb.Vc)} m³`]);
  rows.push(['盛土 累計', `${fmtN(eb.Vf * e)} / ${fmtN(eb.Vf)} m³`]);
  rows.push(['場外ダンプ 累計', `${fmtN((eb.tripsOut + eb.tripsIn) * e)} 台`]);
  const daily = (p.days / 7) * (1 - p.rain / 100) * (winter ? p.winterF / 100 : 1);
  rows.push([
    '稼働日係数',
    `${daily.toFixed(2)}（雨天${p.rain}%${winter ? `・冬期${p.winterF}%` : ''}）`,
  ]);
  rows.push([
    '稼働重機',
    e > 0.001 && e < 0.999 ? `BH ${p.ex}・DT ${p.trucks}・転圧 ${p.rollers}` : '（土工なし）',
  ]);
  s.tasks.forEach((t, i) => {
    if (!t.earth) rows.push([t.name, `${(taskProgress(s, i, T, v.cur) * 100).toFixed(0)}%`]);
  });
  rows.push(['竣工予測', `${fds(v.cur.end)}（${plus(v.cur.delay)}${v.cur.delay}日）`]);
  return `<table class="t">${rows.map(([a, b]) => `<tr><td style="color:var(--muted)">${esc(a)}</td><td>${esc(b)}</td></tr>`).join('')}</table>`;
}

/** 初期計画と現在の計画の比較（サンプルの wmWhatIfHTML）。 */
export function wmWhatIfHtml(v: SiteView): string {
  const a = v.base;
  const b = v.cur;
  const et = v.site.tasks[v.site.earthIdx];
  const done = !et || et.a >= 1;
  const df = (x: number, y: number) => {
    const d = y - x;
    return d === 0 ? '±0' : `${plus(d)}${d}`;
  };
  const cmp = (x: number, y: number) => (y < x ? 'ahead' : y > x ? 'late' : '');
  const row = (
    n: string,
    x: number,
    y: number,
    fmt: (q: number) => string,
    unit: string,
    better: 'lower' | 'higher',
  ) => {
    const d = y - x;
    const zero = Math.abs(d) < 1e-9;
    const cls = zero ? '' : (better === 'lower' ? d < 0 : d > 0) ? 'ahead' : 'late';
    return `<tr><td>${n}</td><td class="n">${fmt(x)}${unit}</td><td class="n">${fmt(y)}${unit}</td><td class="n ${cls}">${zero ? '—' : `${plus(d)}${fmt(d)}`}</td></tr>`;
  };
  const ae = a.earthEnd;
  const be = b.earthEnd;
  const earthRow = done
    ? '<tr><td>土工完了</td><td class="n">完了</td><td class="n">完了</td><td class="n ">—</td></tr>'
    : ae === null || be === null
      ? `<tr><td>土工完了</td><td class="n">${ae === null ? '—' : fds(ae)}</td><td class="n">${be === null ? '—' : fds(be)}</td><td class="n ">—</td></tr>`
      : `<tr><td>土工完了</td><td class="n">${fds(ae)}</td><td class="n">${fds(be)}</td><td class="n ${cmp(ae, be)}">${df(ae, be)}日</td></tr>`;
  return `<table class="t"><tr><th>指標</th><th class="n">初期計画</th><th class="n">現在の a</th><th class="n">差</th></tr>
    ${earthRow}
    <tr><td>竣工予測</td><td class="n">${fds(a.end)}</td><td class="n">${fds(b.end)}</td><td class="n ${cmp(a.end, b.end)}">${df(a.end, b.end)}日</td></tr>
    ${row('切土量', a.eb.Vc, b.eb.Vc, fmtN, '', 'lower')}${row('残土（ほぐし）', a.eb.outLoose, b.eb.outLoose, fmtN, '', 'lower')}
    ${row('実効施工量', a.res.rate, b.res.rate, fmtN, '', 'higher')}${row('場外ダンプ/日', a.res.dailyExt, b.res.dailyExt, fmt1, '', 'lower')}${row('CO₂（土工）', a.res.co2, b.res.co2, fmt1, 't', 'lower')}</table>
    <div class="note">${done ? '土工は完了済のため、編成変更は工程に影響しません（計画地盤高の変更は土量のみ再計算）。' : `ボトルネック：${esc(b.cap.bn)}。計画地盤高を上下させると切盛バランスと残土量が変わります。`}</div>`;
}

// ---- 全国のポートフォリオ（サンプルの portfolioHTML） ----

export function portfolioHtml(store: Store): string {
  return `<div class="g2b"><div class="card"><h3>現場別サマリ <small>${ds(store.state.T)} 時点・行クリックで現場へ</small></h3><div id="pfBody">${portfolioTableHtml(store)}</div></div>
    <div class="card"><h3>地域条件（現場のある都道府県） <small>サンプル値</small></h3>${regionTableHtml(store)}<h3 style="margin-top:12px">全現場の工程 <small>灰＝計画／色＝予測</small></h3><div id="pfGantt">${portfolioGantt(store.inPref(), store.state.T)}</div></div></div>`;
}

export function portfolioTableHtml(store: Store): string {
  const rows = store
    .inPref()
    .map((v) => {
      const s = v.site;
      const st = v.state;
      const d = v.cur.delay;
      return `<tr class="click" data-id="${esc(s.id)}"><td><span class="dot" style="background:${safeColor(s.color)}"></span> ${esc(s.id)}</td><td><div class="pfname" title="${esc(s.name)}">${esc(s.name)}</div></td><td class="nw">${esc(s.pref)}</td>
      <td class="n">${Math.round(st.overall * 100)}%</td><td class="n">${Math.round(st.plan * 100)}%</td><td class="n">${fmtN(s.g.Vc)}</td><td class="n">${fmtN(s.g.Vf)}</td><td class="n">${fmtN(v.cur.eb.outLoose)}</td>
      <td class="nw ${d > 14 ? 'late' : d > 0 ? 'warnc' : 'ahead'}">${fds(v.cur.end)}（${plus(d)}${d}日）</td></tr>`;
    })
    .join('');
  return `<table class="t"><tr><th>ID</th><th>工事名</th><th>都道府県</th><th class="n">進捗</th><th class="n">計画</th><th class="n">切土</th><th class="n">盛土</th><th class="n">残土</th><th>予測竣工</th></tr>${rows}</table><div class="note">土量 m³（切土：地山、盛土：締固め後、残土：ほぐし）。</div>`;
}

export function regionTableHtml(store: Store): string {
  const prefs = [...new Set(store.inPref().map((v) => v.site.pref))];
  const rows = prefs
    .map((p) => {
      const r = REGIONS[p];
      if (!r) return '';
      return `<tr><td>${esc(p)}</td><td class="n">${r.rain}%</td><td>${r.ws ? `${r.ws}〜${r.we}月（${Math.round(r.wf * 100)}%）` : '—'}</td><td class="n">${r.dExt}km</td></tr>`;
    })
    .join('');
  return `<table class="t"><tr><th>都道府県</th><th class="n">雨天休止</th><th>冬期（土工稼働）</th><th class="n">残土距離</th></tr>${rows}</table>`;
}
